import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PeopleIcon from '@mui/icons-material/People';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useReferrals } from '../hooks/useReferrals';
import { usePlan } from '../hooks/usePlan';
import type { ReferralStatus } from '../types';

function getStatusColor(status: ReferralStatus): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'expired':
      return 'default';
  }
}

function getStatusLabel(status: ReferralStatus): string {
  switch (status) {
    case 'completed':
      return 'Concluida';
    case 'pending':
      return 'Pendente';
    case 'expired':
      return 'Expirada';
  }
}

export function ReferralDashboardPage(): React.ReactElement {
  const { dashboard, history, isLoading, error } = useReferrals();
  const { planInfo } = usePlan();
  const [toastOpen, setToastOpen] = useState(false);

  const handleCopyLink = async () => {
    if (dashboard?.link.url) {
      await navigator.clipboard.writeText(dashboard.link.url);
      setToastOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const photoUsagePercent = planInfo
    ? Math.min((planInfo.photosUsed / planInfo.photoQuota) * 100, 100)
    : 0;

  return (
    <Box p={3} maxWidth="1200px" mx="auto">
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Programa de Indicação
      </Typography>

      {/* Referral Link Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Seu Link de Indicação
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {dashboard?.link.url}
            </Typography>
            <Tooltip title="Copiar link">
              <IconButton color="primary" onClick={handleCopyLink} aria-label="Copiar link de indicação">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={3} mb={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight={700} mt={1}>
              {dashboard?.totalReferrals ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total de Indicacoes
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <CardGiftcardIcon color="secondary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight={700} mt={1}>
              {dashboard?.totalBonusQuota ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fotos Bonus Ganhas
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <PhotoLibraryIcon color="info" sx={{ fontSize: 40 }} />
            <Typography variant="h4" fontWeight={700} mt={1}>
              {planInfo?.photosUsed ?? 0} / {planInfo?.photoQuota ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fotos Utilizadas
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Quota Usage */}
      {planInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Uso da Quota
            </Typography>
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Fotos</Typography>
                <Typography variant="body2">
                  {planInfo.photosUsed} / {planInfo.photoQuota}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={photoUsagePercent}
                color={photoUsagePercent > 90 ? 'error' : photoUsagePercent > 70 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            {planInfo.limits.maxCountries !== null && (
              <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Países</Typography>
                  <Typography variant="body2">
                    {planInfo.countriesVisited} / {planInfo.limits.maxCountries}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(
                    (planInfo.countriesVisited / planInfo.limits.maxCountries) * 100,
                    100
                  )}
                  color="secondary"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Plano atual:
              </Typography>
              <Chip
                label={planInfo.plan.charAt(0).toUpperCase() + planInfo.plan.slice(1)}
                color="primary"
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Referral History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Historico de Indicacoes
          </Typography>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma indicação realizada ainda. Compartilhe seu link para começar!
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Bonus</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.referredEmail}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(item.status)}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        +{item.bonusCreditedReferrer} fotos
                      </TableCell>
                      <TableCell>
                        {new Date(item.completedAt ?? item.createdAt).toLocaleDateString('pt-BR')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Toast for copy */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled">
          Link copiado para a area de transferencia!
        </Alert>
      </Snackbar>
    </Box>
  );
}
